import {
  CalculationResult,
  DEFAULT_INPUTS,
  Inputs,
  RowData,
  activeRepsForRow,
  calculateModel,
  getLeaderMonths,
  getPricing,
  DOUBLE_ATTRITION_SURVIVAL,
} from './roiCalculations'
import { RepEconomics, calculateRepEconomics, round1 } from './repEconomics'

// Builds everything the Financial Model PDF shows from one set of calculator
// inputs. Scenario math follows the calculator exactly: 50% sales and 2x
// attrition each halve gross revenue and profit. 2x attrition also halves the
// estimated bonuses (fewer reps survive to earn them); fees and ads stay.

export interface ModelScenario {
  halfSales: boolean
  doubleAttrition: boolean
}

export interface ScenarioSummary {
  investment: number
  grossRevenue: number
  grossProfit: number
  net: number
  roi: number
  investment24: number
  net24: number
  roi24: number
  /** Row index where the cumulative position first turns positive, or null. */
  breakevenMonth: number | null
  /** That row's label (e.g. "Month 2"), or null. */
  breakevenLabel: string | null
  /** Cumulative net position at the end of Build (0) through Month 12. */
  cumulative: number[]
}

export interface ScenarioRow {
  key: string
  label: string
  scenario: ModelScenario
  summary: ScenarioSummary
  active: boolean
}

/** Page 3's chain. Every step recomputes from the one before it. */
export interface ModelChain {
  /** Campaign-month ad spend (search ads are counted in costs, not here). */
  adSpend: number
  /** Rep hires in campaign Months 1–12 (the leader hire is separate). */
  hires: number
  /** hires × launch rate, one decimal. */
  launched: number
  /** Hires and launched reps from Months 1–9, the cohorts that can reach day 90. */
  hiresThroughMonth9: number
  launchedThroughMonth9: number
  /** Last campaign month whose hires can reach day 90 inside the window. */
  day90CutoffMonth: number
  /** Share of launched reps reaching day 90 (day-30 × day-30-to-90), 0–100. */
  survivalPct: number
  /** launchedThroughMonth9 × survival, one decimal. */
  ninetyDayReps: number
  /** Full-year totals (campaign reps + the leader). */
  sales: number
  grossProfit: number
  /** The leader's share of those totals (0 without a search). */
  leaderSales: number
  leaderGrossProfit: number
}

/** One line of the simplified monthly projection (page 3). */
export interface ProjectionLine {
  label: string
  hires: number
  activeReps: number
  sales: number
  revenue: number
  grossProfit: number
  hireFees: number
  adSpend: number
  bonuses: number
  totalCost: number
  net: number
  cumulative: number
}

export interface FinancialModelData {
  inputs: Inputs
  /** Leader search months modeled before campaign Month 1 (0 = none). */
  leaderMonths: number
  /** The first month's fees sit in the Implementation row (due at signing). */
  firstMonthPrepaid: boolean
  /** "Implementation through Month 12". */
  windowLabel: string
  /** Short axis labels for the chart: Build, S1, S2, M1… */
  axisLabels: string[]
  /** Build + any search months + Months 1–12, with the scenario applied. */
  projection: ProjectionLine[]
  /** Totals for the model window and for the months after it. */
  projectionYearOne: ProjectionLine
  projectionYearTwo: ProjectionLine
  scenario: ModelScenario
  scenarioLabel: string
  summary: ScenarioSummary
  scenarios: ScenarioRow[]
  chain: ModelChain
  rep: RepEconomics | null
  monthlyAdSpend: number
  cphLow: number
  cphHigh: number
}

const HORIZON = 12

const profitMultiplier = (s: ModelScenario) =>
  (s.halfSales ? 0.5 : 1) * (s.doubleAttrition ? 0.5 : 1)

export const scenarioLabel = (s: ModelScenario) =>
  s.halfSales && s.doubleAttrition
    ? 'Sales halved + attrition doubled'
    : s.halfSales
      ? 'Sales cut in half'
      : s.doubleAttrition
        ? 'Attrition doubled'
        : 'Your model'

// Doubled attrition: half as many reps survive each stage, so the estimated
// bonuses on them drop by the same share. Fees and ad spend don't change.
const bonusMultiplier = (s: ModelScenario) =>
  s.doubleAttrition ? DOUBLE_ATTRITION_SURVIVAL : 1

const rowCost = (row: RowData, s: ModelScenario) =>
  row.totalCost - row.bonuses * (1 - bonusMultiplier(s))

// Row labels: Implementation (signing), then months counted straight from
// signing. Leader search months are tagged "(leader)", so after a two-month
// search the rep campaign starts in Month 3.
export const rowLabel = (index: number, leaderMonths = 0) =>
  index === 0
    ? 'Implementation'
    : index <= leaderMonths
      ? `Month ${index} (leader)`
      : `Month ${index}`

const shortRowLabel = (index: number, leaderMonths: number) =>
  index === 0 ? 'Impl.' : index <= leaderMonths ? `M${index} (L)` : `M${index}`

/** Months always run 1–12 from signing, leader search included. */
export const lastWindowMonth = (_leaderMonths = 0) => HORIZON

function summarize(
  result: CalculationResult,
  scenario: ModelScenario,
  leaderMonths: number,
): ScenarioSummary {
  const horizon = HORIZON
  const mult = profitMultiplier(scenario)
  // Implementation + 24 months; the model itself runs longer.
  const rows = result.rows.slice(0, 25)
  let investment = 0
  let grossRevenue = 0
  let grossProfit = 0
  let investment24 = 0
  let grossProfit24 = 0
  let running = 0
  let breakevenMonth: number | null = null
  const cumulative: number[] = []
  rows.forEach((row, index) => {
    const gp = row.grossProfit * mult
    const cost = rowCost(row, scenario)
    running += gp - cost
    investment24 += cost
    grossProfit24 += gp
    if (index <= horizon) {
      investment += cost
      grossRevenue += row.grossRevenue * mult
      grossProfit += gp
      cumulative.push(running)
    }
    if (breakevenMonth === null && index >= 1 && running >= 0) {
      breakevenMonth = index
    }
  })
  const net = grossProfit - investment
  const net24 = grossProfit24 - investment24
  return {
    investment,
    grossRevenue,
    grossProfit,
    net,
    // ROI = (gain − cost) ÷ cost
    roi: investment > 0 ? (net / investment) * 100 : 0,
    investment24,
    net24,
    roi24: investment24 > 0 ? (net24 / investment24) * 100 : 0,
    breakevenMonth,
    breakevenLabel:
      breakevenMonth === null ? null : rowLabel(breakevenMonth, leaderMonths),
    cumulative,
  }
}

// "Attrition doubled" alone lands on the same numbers as "sales cut in half",
// so the stress test shows half sales, then half sales + double attrition.
const SCENARIOS: { key: string; scenario: ModelScenario }[] = [
  { key: 'base', scenario: { halfSales: false, doubleAttrition: false } },
  { key: 'half', scenario: { halfSales: true, doubleAttrition: false } },
  { key: 'both', scenario: { halfSales: true, doubleAttrition: true } },
]

function projectionLines(
  rows: RowData[],
  scenario: ModelScenario,
  leaderMonths: number,
): ProjectionLine[] {
  const mult = profitMultiplier(scenario)
  const retention = scenario.doubleAttrition ? 0.5 : 1
  let running = 0
  return rows.map((row, index) => {
    const grossProfit = row.grossProfit * mult
    const bonuses = row.bonuses * bonusMultiplier(scenario)
    const totalCost = rowCost(row, scenario)
    running += grossProfit - totalCost
    // The leader is hired in the last search month and is active for that
    // whole month, so they count as one full active rep there (new launches
    // otherwise count at half weight).
    const isLeaderHire = leaderMonths > 0 && index === leaderMonths
    return {
      label: rowLabel(index, leaderMonths),
      hires: row.hires,
      activeReps:
        activeRepsForRow(row) * retention +
        (isLeaderHire ? row.launched * 0.5 * retention : 0),
      sales: row.totalSales * mult,
      revenue: row.grossRevenue * mult,
      grossProfit,
      hireFees: row.hireCost,
      adSpend: row.adSpend,
      bonuses,
      totalCost,
      net: grossProfit - totalCost,
      cumulative: running,
    }
  })
}

function totalLine(label: string, lines: ProjectionLine[]): ProjectionLine {
  const sum = (key: keyof ProjectionLine) =>
    lines.reduce((t, l) => t + (l[key] as number), 0)
  const last = lines[lines.length - 1]
  return {
    label,
    hires: sum('hires'),
    activeReps: last?.activeReps ?? 0,
    sales: sum('sales'),
    revenue: sum('revenue'),
    grossProfit: sum('grossProfit'),
    hireFees: sum('hireFees'),
    adSpend: sum('adSpend'),
    bonuses: sum('bonuses'),
    totalCost: sum('totalCost'),
    net: sum('net'),
    cumulative: last?.cumulative ?? 0,
  }
}

export function buildFinancialModel(
  inputs: Inputs,
  scenario: ModelScenario,
): FinancialModelData {
  const result = calculateModel({
    ...inputs,
    buildPay: inputs.buildPay || 'upfront',
  })
  const leaderMonths = getLeaderMonths(inputs)
  // The window is always the first year; a leader search sits inside it.
  const horizon = HORIZON
  const rows = result.rows.slice(0, horizon + 1)
  const campaignRows = result.rows.slice(leaderMonths + 1, horizon + 1)
  const mult = profitMultiplier(scenario)
  const retention = scenario.doubleAttrition ? 0.5 : 1
  const rep = calculateRepEconomics(inputs, result, scenario)

  // Each step is computed from the rounded figure printed before it, so the
  // chain can be checked by hand.
  const hires = campaignRows.reduce((t, r) => t + r.hires, 0)
  const hiresThroughMonth9 = campaignRows
    // Hires through Month 9 can reach day 90 by Month 12.
    .slice(0, Math.max(0, HORIZON - 3 - leaderMonths))
    .reduce((t, r) => t + r.hires, 0)
  const launchRate = Math.min(1, Math.max(0, inputs.pctLaunched / 100))
  const survival =
    Math.min(1, Math.max(0, inputs.pct30 / 100)) *
    Math.min(1, Math.max(0, inputs.pct90 / 100)) *
    retention
  const launchedThroughMonth9 = round1(hiresThroughMonth9 * launchRate)
  const sales = rows.reduce((t, r) => t + r.totalSales, 0) * mult
  const grossProfit = rows.reduce((t, r) => t + r.grossProfit, 0) * mult
  // Split the leader's share out of the year's sales and gross profit by
  // running the same model without their production.
  let leaderSales = 0
  let leaderGrossProfit = 0
  if (leaderMonths > 0) {
    const withoutLeader = calculateModel({
      ...inputs,
      buildPay: inputs.buildPay || 'upfront',
      excludeLeaderSales: true,
    }).rows.slice(0, horizon + 1)
    leaderSales =
      sales - withoutLeader.reduce((t, r) => t + r.totalSales, 0) * mult
    leaderGrossProfit =
      grossProfit - withoutLeader.reduce((t, r) => t + r.grossProfit, 0) * mult
  }
  const chain: ModelChain = {
    adSpend: campaignRows.reduce((t, r) => t + r.adSpend, 0),
    hires,
    launched: round1(hires * launchRate),
    hiresThroughMonth9,
    launchedThroughMonth9,
    day90CutoffMonth: Math.max(0, HORIZON - 3 - leaderMonths),
    survivalPct: survival * 100,
    ninetyDayReps: round1(launchedThroughMonth9 * survival),
    sales,
    grossProfit,
    leaderSales,
    leaderGrossProfit,
  }

  const allLines = projectionLines(result.rows, scenario, leaderMonths)
  const windowLines = allLines.slice(0, horizon + 1)
  const lastModelMonth = 24
  const lastMonth = lastWindowMonth(leaderMonths)
  const pricing = getPricing(inputs)

  return {
    inputs,
    leaderMonths,
    firstMonthPrepaid: !!inputs.pkg && pricing.firstMonthInBuild,
    windowLabel: `Implementation through Month ${lastMonth}`,
    axisLabels: windowLines.map((_, index) =>
      shortRowLabel(index, leaderMonths),
    ),
    projection: windowLines,
    projectionYearOne: totalLine(
      'Year one total',
      windowLines,
    ),
    projectionYearTwo: totalLine(
      `Months ${lastMonth + 1}–${lastModelMonth - leaderMonths}`,
      allLines.slice(horizon + 1, lastModelMonth + 1),
    ),
    scenario,
    scenarioLabel: scenarioLabel(scenario),
    summary: summarize(result, scenario, leaderMonths),
    scenarios: SCENARIOS.map((s) => ({
      key: s.key,
      label: scenarioLabel(s.scenario),
      scenario: s.scenario,
      summary: summarize(result, s.scenario, leaderMonths),
      active:
        s.scenario.halfSales === scenario.halfSales &&
        s.scenario.doubleAttrition === scenario.doubleAttrition,
    })),
    chain,
    rep,
    monthlyAdSpend: inputs.adSpend * inputs.campaigns,
    cphLow: inputs.cphLow ?? Math.max(1, inputs.cph - 150),
    cphHigh: inputs.cphHigh ?? inputs.cph + 150,
  }
}

/** Calculator inputs built from the deck's settings, used until the
 *  calculator itself has been changed. */
export function inputsFromDeck(deck: {
  adSpend: number
  cpaLow: number
  cpaHigh: number
}): Inputs {
  const campaigns = Math.max(1, Math.round(deck.adSpend / 2500))
  const perCampaign = deck.adSpend / campaigns
  return {
    ...DEFAULT_INPUTS,
    campaigns,
    monthlyCampaigns: Array(12).fill(campaigns),
    adSpend: perCampaign,
    monthlyAdSpend: Array(12).fill(perCampaign),
    cphLow: deck.cpaLow,
    cphHigh: deck.cpaHigh,
    cph: Math.round((deck.cpaLow + deck.cpaHigh) / 2),
  }
}

// Formatting for the printed model.
export const usd = (value: number) => {
  const rounded = Math.round(value)
  const text = '$' + Math.abs(rounded).toLocaleString('en-US')
  return rounded < 0 ? '−' + text : text
}

export const compactUsd = (value: number) => {
  const abs = Math.abs(value)
  const sign = value < 0 ? '−' : ''
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 1 : 2)}M`
  if (abs >= 10_000) return `${sign}$${Math.round(abs / 1000)}K`
  return usd(value)
}

export const pct = (value: number) => `${Math.round(value)}%`

export const count = (value: number) =>
  value >= 100 ? Math.round(value).toLocaleString('en-US') : value.toFixed(value % 1 === 0 ? 0 : 1)
